import { useState, useEffect, useRef, useCallback } from 'react'
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns'
import {
  Clock,
  MapPin,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  PlusCircle,
  RefreshCw,
  Camera,
  X,
} from 'lucide-react'
import {
  getAttendanceHistory,
  getLeaveTypes,
  getMyLeaves,
  createLeave,
  AttendanceHistory,
  LeaveType,
  LeaveRequest,
  CreateLeaveData,
} from '../../services/eagleVision'
import PullToRefresh from '../../components/common/PullToRefresh'
import { useAttendance } from '../../hooks/useAttendance'
import { usePermissions } from '../../hooks/usePermissions'

type TabType = 'attendance' | 'history' | 'leave'

export default function AttendancePage() {
  const { canAccessPage } = usePermissions()
  const showLeaveTab = canAccessPage('pwa_leave')

  const [activeTab, setActiveTab] = useState<TabType>('attendance')

  const attendance = useAttendance()
  const {
    summary,
    summaryError,
    isLoading,
    needsAuth,
    isCheckingIn,
    isCheckingOut,
    hasCheckedIn,
    hasCheckedOut,
    displayCheckIn,
    displayCheckOut,
    location,
    locationLoading,
    locationError,
    result,
    setResult,
    error,
    setError,
    handleCheckIn,
    handleCheckOut,
    fetchSummary,
  } = attendance

  // Selfie camera state
  const [cameraAction, setCameraAction] = useState<'checkin' | 'checkout' | null>(null)
  const [cameraReady, setCameraReady] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  // History state
  const [history, setHistory] = useState<AttendanceHistory | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyMonth, setHistoryMonth] = useState(new Date())

  // Leave state
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([])
  const [myLeaves, setMyLeaves] = useState<LeaveRequest[]>([])
  const [showLeaveForm, setShowLeaveForm] = useState(false)
  const [leaveForm, setLeaveForm] = useState<CreateLeaveData>({
    leave_type_id: 0,
    start_date: '',
    end_date: '',
    reason: '',
    is_half_day: false,
  })
  const [isSubmittingLeave, setIsSubmittingLeave] = useState(false)

  const getErrorMessage = (err: unknown, fallback: string): string => {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      return axiosErr.response?.data?.message || fallback
    }
    return fallback
  }

  // Selfie camera functions (matching PersonalDashboard)
  const openCamera = async (action: 'checkin' | 'checkout') => {
    setCameraAction(action)
    setCameraReady(false)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      })
      streamRef.current = stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.play()
          setCameraReady(true)
        }
      }, 100)
    } catch {
      closeCamera()
      if (action === 'checkin') handleCheckIn()
      else handleCheckOut()
    }
  }

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    setCameraAction(null)
    setCameraReady(false)
  }

  const captureAndSubmit = () => {
    const video = videoRef.current
    const action = cameraAction
    if (!video || !action) return

    const canvas = document.createElement('canvas')
    canvas.width = Math.min(video.videoWidth, 320)
    canvas.height = Math.min(video.videoHeight, 240)
    const ctx = canvas.getContext('2d')
    if (!ctx) { closeCamera(); return }

    // Mirror the image (selfie)
    ctx.translate(canvas.width, 0)
    ctx.scale(-1, 1)
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    const base64 = canvas.toDataURL('image/jpeg', 0.4).split(',')[1]

    closeCamera()
    if (action === 'checkin') handleCheckIn(base64)
    else handleCheckOut(base64)
  }

  // Fetch attendance history from local database
  const fetchHistory = async () => {
    setHistoryLoading(true)
    setError(null)
    try {
      const startDate = format(startOfMonth(historyMonth), 'yyyy-MM-dd')
      const endDate = format(endOfMonth(historyMonth), 'yyyy-MM-dd')
      const data = await getAttendanceHistory(startDate, endDate)
      setHistory(data)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load attendance history'))
      setHistory(null)
    } finally {
      setHistoryLoading(false)
    }
  }

  // Fetch leave data
  const fetchLeaveData = async () => {
    try {
      const [types, leaves] = await Promise.all([getLeaveTypes(), getMyLeaves()])
      setLeaveTypes(types)
      setMyLeaves(leaves.data)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load leave data'))
    }
  }

  // Load history when tab changes or month changes
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory()
    }
  }, [activeTab, historyMonth])

  // Load leave data when tab changes
  useEffect(() => {
    if (activeTab === 'leave') {
      fetchLeaveData()
    }
  }, [activeTab])

  // Handle leave submission
  const handleSubmitLeave = async () => {
    if (!leaveForm.leave_type_id || !leaveForm.start_date || !leaveForm.end_date) {
      setError('Please fill in all required fields')
      return
    }

    setIsSubmittingLeave(true)
    setError(null)
    try {
      await createLeave(leaveForm)
      setShowLeaveForm(false)
      setLeaveForm({
        leave_type_id: 0,
        start_date: '',
        end_date: '',
        reason: '',
        is_half_day: false,
      })
      setResult({
        type: 'success',
        title: 'Leave Submitted',
        message: 'Your leave request has been submitted for approval.',
      })
      await fetchLeaveData()
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to submit leave request'))
    } finally {
      setIsSubmittingLeave(false)
    }
  }

  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchSummary(),
      activeTab === 'history' ? fetchHistory() : Promise.resolve(),
      activeTab === 'leave' ? fetchLeaveData() : Promise.resolve(),
    ])
  }, [activeTab])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'present': return 'text-green-600 bg-green-100'
      case 'absent': return 'text-red-600 bg-red-100'
      case 'late': return 'text-yellow-600 bg-yellow-100'
      case 'on_leave': return 'text-blue-600 bg-blue-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getLeaveStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100'
      case 'rejected': return 'text-red-600 bg-red-100'
      case 'pending': return 'text-yellow-600 bg-yellow-100'
      case 'cancelled': return 'text-gray-600 bg-gray-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  // Safe date formatting helper
  const safeFormatDate = (dateStr: string, fmt: string, fallback = '--') => {
    try {
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return fallback
      return format(d, fmt)
    } catch {
      return fallback
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (needsAuth) {
    return (
      <div className="p-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-yellow-500 mb-2" />
          <h3 className="font-medium text-yellow-800">Eagle Vision Authentication Required</h3>
          <p className="text-sm text-yellow-700 mt-1">
            Please logout and login with your Eagle Vision credentials to access attendance features.
          </p>
        </div>
      </div>
    )
  }

  return (
    <PullToRefresh onRefresh={refreshAll}>
    <div className="p-4 pb-24">
      <h1 className="text-xl font-bold mb-4">Attendance</h1>

      {/* Tabs */}
      <div className="flex rounded-lg bg-gray-100 p-1 mb-4">
        <button
          onClick={() => { setActiveTab('attendance'); setError(null) }}
          className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'attendance' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600'
          }`}
        >
          Today
        </button>
        <button
          onClick={() => { setActiveTab('history'); setError(null) }}
          className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'history' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600'
          }`}
        >
          History
        </button>
        {showLeaveTab && (
          <button
            onClick={() => { setActiveTab('leave'); setError(null) }}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'leave' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600'
            }`}
          >
            Leave
          </button>
        )}
      </div>

      {/* Result Notification */}
      {result && (
        <div
          className={`mb-4 p-4 rounded-lg border ${
            result.type === 'success'
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-start gap-3">
            {result.type === 'success' ? (
              <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <h3 className={`font-semibold ${result.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                {result.title}
              </h3>
              <p className={`text-sm mt-0.5 ${result.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                {result.message}
              </p>
            </div>
            <button onClick={() => setResult(null)} className="text-gray-400 hover:text-gray-600">
              <XCircle className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Error notification */}
      {error && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Today's Attendance Tab */}
      {activeTab === 'attendance' && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-4">
            {/* Date and status header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500">
                  {format(new Date(), 'EEEE, MMMM d, yyyy')}
                </p>
                {summary?.status && (
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${getStatusColor(summary.status)}`}
                  >
                    {summary.status.replace('_', ' ').toUpperCase()}
                  </span>
                )}
              </div>
              {summary?.shift ? (
                <div className="text-right">
                  <p className="text-sm text-gray-500">Shift</p>
                  <p className="font-medium">{summary.shift.name}</p>
                  <p className="text-xs text-gray-500">
                    {summary.shift.start_time} - {summary.shift.end_time}
                  </p>
                </div>
              ) : summaryError ? (
                <button
                  onClick={() => fetchSummary()}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Retry
                </button>
              ) : null}
            </div>

            {/* Check-in/out times */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <Clock className="h-4 w-4" />
                  Check In
                </div>
                <p className="font-semibold text-lg">
                  {displayCheckIn ? format(new Date(displayCheckIn), 'HH:mm') : '--:--'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <Clock className="h-4 w-4" />
                  Check Out
                </div>
                <p className="font-semibold text-lg">
                  {displayCheckOut ? format(new Date(displayCheckOut), 'HH:mm') : '--:--'}
                </p>
              </div>
            </div>

            {/* Working hours */}
            {summary?.working_hours !== undefined && summary.working_hours > 0 && (
              <div className="text-center py-2 bg-green-50 rounded-lg mb-4">
                <p className="text-sm text-green-700">
                  Working Hours: <span className="font-semibold">{summary.working_hours.toFixed(2)} hrs</span>
                </p>
              </div>
            )}

            {/* Location info */}
            <div className="mb-4">
              <div className="flex items-center gap-1 text-sm text-gray-500 mb-1">
                <MapPin className="h-4 w-4" />
                Location
              </div>
              {locationLoading ? (
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Getting GPS location...
                </p>
              ) : location ? (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  GPS: {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                </p>
              ) : (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {locationError || 'GPS unavailable - check-in/out requires GPS'}
                </p>
              )}
            </div>

            {/* Action buttons (selfie camera like dashboard) */}
            {!(hasCheckedIn && hasCheckedOut) ? (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => openCamera('checkin')}
                  disabled={isCheckingIn || hasCheckedIn || !!cameraAction || !location}
                  className={`py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                    hasCheckedIn || !location
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
                  }`}
                >
                  {isCheckingIn ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : locationLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Camera className="h-5 w-5" />
                  )}
                  {isCheckingIn ? 'Checking In...' : locationLoading ? 'GPS...' : !location ? 'No GPS' : 'Check In'}
                </button>
                <button
                  onClick={() => openCamera('checkout')}
                  disabled={isCheckingOut || !hasCheckedIn || hasCheckedOut || !!cameraAction || !location}
                  className={`py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                    !hasCheckedIn || hasCheckedOut || !location
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800'
                  }`}
                >
                  {isCheckingOut ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Camera className="h-5 w-5" />
                  )}
                  {isCheckingOut ? 'Checking Out...' : 'Check Out'}
                </button>
              </div>
            ) : (
              <div className="text-center py-3 bg-green-50 rounded-lg">
                <CheckCircle className="h-8 w-8 mx-auto text-green-600 mb-1" />
                <p className="text-sm font-medium text-green-700">Attendance completed for today</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {/* Month selector */}
          <div className="flex items-center justify-between bg-white rounded-lg shadow p-3">
            <button
              onClick={() => setHistoryMonth(subMonths(historyMonth, 1))}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="font-medium">{format(historyMonth, 'MMMM yyyy')}</span>
            <button
              onClick={() => setHistoryMonth(new Date(historyMonth.getFullYear(), historyMonth.getMonth() + 1))}
              className="p-2 hover:bg-gray-100 rounded-lg"
              disabled={historyMonth >= new Date()}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : (
            <>
              {/* Summary */}
              {history?.summary && (
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-green-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-green-600">{history.summary.present}</p>
                    <p className="text-xs text-green-700">Present</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-red-600">{history.summary.absent}</p>
                    <p className="text-xs text-red-700">Absent</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-yellow-600">{history.summary.late}</p>
                    <p className="text-xs text-yellow-700">Late</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-blue-600">{history.summary.leaves}</p>
                    <p className="text-xs text-blue-700">Leave</p>
                  </div>
                </div>
              )}

              {/* History list */}
              <div className="bg-white rounded-lg shadow divide-y">
                {history?.data?.map((record) => (
                  <div key={record.id} className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{safeFormatDate(record.date, 'EEE, MMM d')}</p>
                      <p className="text-sm text-gray-500">
                        {record.check_in ? safeFormatDate(record.check_in, 'HH:mm') : '--:--'} -{' '}
                        {record.check_out ? safeFormatDate(record.check_out, 'HH:mm') : '--:--'}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                        {record.status}
                      </span>
                      {record.working_hours !== undefined && record.working_hours > 0 && (
                        <p className="text-xs text-gray-500 mt-1">{record.working_hours.toFixed(1)} hrs</p>
                      )}
                    </div>
                  </div>
                ))}
                {(!history?.data || history.data.length === 0) && (
                  <div className="p-8 text-center text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No attendance records for this month</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Leave Tab */}
      {activeTab === 'leave' && showLeaveTab && (
        <div className="space-y-4">
          <button
            onClick={() => setShowLeaveForm(true)}
            className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-primary-700"
          >
            <PlusCircle className="h-5 w-5" />
            Request Leave
          </button>

          {showLeaveForm && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-medium mb-4">New Leave Request</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Leave Type *</label>
                  <select
                    value={leaveForm.leave_type_id}
                    onChange={(e) => setLeaveForm({ ...leaveForm, leave_type_id: Number(e.target.value) })}
                    className="input"
                  >
                    <option value={0}>Select leave type</option>
                    {leaveTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Start Date *</label>
                    <input
                      type="date"
                      value={leaveForm.start_date}
                      onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">End Date *</label>
                    <input
                      type="date"
                      value={leaveForm.end_date}
                      onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={leaveForm.is_half_day}
                      onChange={(e) => setLeaveForm({ ...leaveForm, is_half_day: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Half day leave</span>
                  </label>
                </div>

                {leaveForm.is_half_day && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Half Day Type</label>
                    <select
                      value={leaveForm.half_day_type || ''}
                      onChange={(e) =>
                        setLeaveForm({
                          ...leaveForm,
                          half_day_type: e.target.value as 'first_half' | 'second_half',
                        })
                      }
                      className="input"
                    >
                      <option value="">Select</option>
                      <option value="first_half">First Half</option>
                      <option value="second_half">Second Half</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Reason</label>
                  <textarea
                    value={leaveForm.reason}
                    onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                    rows={3}
                    className="input"
                    placeholder="Enter reason for leave"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowLeaveForm(false)}
                    className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitLeave}
                    disabled={isSubmittingLeave}
                    className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isSubmittingLeave && <Loader2 className="h-4 w-4 animate-spin" />}
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow divide-y">
            <div className="p-3 font-medium text-gray-700">My Leave Requests</div>
            {myLeaves.map((leave) => (
              <div key={leave.id} className="p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{leave.leave_type?.name || 'Leave'}</p>
                    <p className="text-sm text-gray-500">
                      {safeFormatDate(leave.start_date, 'MMM d')} - {safeFormatDate(leave.end_date, 'MMM d, yyyy')}
                    </p>
                    {leave.reason && <p className="text-sm text-gray-600 mt-1">{leave.reason}</p>}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLeaveStatusColor(leave.status)}`}>
                    {leave.status}
                  </span>
                </div>
              </div>
            ))}
            {myLeaves.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No leave requests</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selfie Camera Overlay */}
      {cameraAction && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 bg-black/80">
            <span className="text-white text-sm font-medium">
              {cameraAction === 'checkin' ? 'Check In Selfie' : 'Check Out Selfie'}
            </span>
            <button onClick={closeCamera} className="text-white p-1">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
          </div>
          <div className="flex items-center justify-center py-6 bg-black/80">
            <button
              onClick={captureAndSubmit}
              disabled={!cameraReady}
              className="w-16 h-16 rounded-full border-4 border-white flex items-center justify-center disabled:opacity-30 active:scale-95 transition-transform"
            >
              <div className={`w-12 h-12 rounded-full ${cameraAction === 'checkin' ? 'bg-green-500' : 'bg-red-500'}`} />
            </button>
          </div>
        </div>
      )}
    </div>
    </PullToRefresh>
  )
}
